import {
  IGenerateReportCommandInput,
  ISiteLocation,
  IStepSubmission,
  IWorkflowStep,
} from '~/client/graph/types.generated'
import { IAppConfig } from '~/client/src/shared/Config'
import { getWorkflowStepDisplayName } from '~/client/src/shared/localization/enumDisplayTexts'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Sitemap from '~/client/src/shared/models/Sitemap'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import SitemapItemsStore from '~/client/src/shared/stores/domain/SitemapItems.store'
import SitemapsStore from '~/client/src/shared/stores/domain/Sitemaps.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import { DOWNLOAD_REPORT_FILE } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { toast } from '~/client/src/shared/utils/toast'

const FORM_TEMPLATE_ID = 'form_view_report'

interface IEnrichedSubmission {
  stepId: string
  stepName: string
  userId: string
  userName: string
  userCompany: string
  submittedAtFormatted: string
  fields: IStepSubmission['fields']
}

interface IReportSubmissionGroup {
  stepId: string
  stepName: string
  requireAllSubmissions: boolean
  submissions: IEnrichedSubmission[]
}

export default class FormReportGenerationStore {
  public constructor(
    private readonly projectDateStore: ProjectDateStore,
    private readonly eventsStore: EventsStore,
    private readonly configuration: IAppConfig,
    private readonly sitemapItemsStore: SitemapItemsStore,
    private readonly sitemapsStore: SitemapsStore,
    private readonly locationAttributesStore: LocationAttributesStore,
    private readonly projectMembersStore: ProjectMembersStore,
    private readonly workflowTypesStore: WorkflowTypesStore,
    private readonly companiesStore?: CompaniesStore,
    private readonly materialsStore?: MaterialsStore,
    private readonly userProjectsStore?: UserProjectsStore,
  ) {}

  public generateAndSendReport = (form: Workflow, callbackFn?: () => void) => {
    const { getClientTimezoneId, getMonthAndDayToDisplay } =
      this.projectDateStore
    const { activeProject } = this.eventsStore.appState

    const formTypeName = form.getNameOfPermitType(this.workflowTypesStore)
    const now = Date.now()
    const data = {
      timezoneId: getClientTimezoneId(),
      dateTimeFormat: activeProject.dateTimeFormat,
      projectId: activeProject.id,
      materialsUploadId: activeProject.materialsUploadId,
      logoUrl: activeProject.logoUrl,
      isLogoWhite: activeProject.isLogoWhite,
      permit: form.getReportDto(),
      overviewMaps: this.getSitemapImagesForReport(form.locationsWithEquipment),
      qrCodeLink: Workflow.getDirectLinkToInstance(activeProject.code, form.id),
      linkedWorkflowIds: form.linkedWorkflowFields,
      submissionGroups: this.getSubmissionGroupsForReport(form),
    }
    const filename = `${this.configuration.TENANT_ID}_${
      activeProject.name
    }_${getMonthAndDayToDisplay(now)}${Localization.translator.reportOf(
      formTypeName,
    )}`

    const generateReportInput: IGenerateReportCommandInput = {
      templateId: FORM_TEMPLATE_ID,
      files: [{ fileId: '', fileName: `${filename}.pdf` }],
      title: Localization.translator.reportOf(formTypeName),
      dataJSON: JSON.stringify(data),
      projectId: activeProject.id,
      dateTo: now,
      dateFrom: now,
      timezoneId: data.timezoneId,
      sendEmail: true,
      isFullTime: false,
      additionalRecipientIds: [],
    }

    this.eventsStore.dispatch(
      DOWNLOAD_REPORT_FILE,
      generateReportInput,
      callbackFn,
      () => {
        callbackFn?.()
        toast.error(Localization.translator.filedToGenerateReport)
      },
    )
  }

  /**
   * Groups step submissions by step and enriches them with user details and formatted field values.
   * Only includes steps that have requireAllSubmissions enabled. A step signed in several rounds
   * shows its latest one.
   */
  public getSubmissionGroupsForReport(
    form: Workflow,
  ): IReportSubmissionGroup[] {
    const permitType = this.workflowTypesStore.getPermitTypeById(form.typeId)
    if (!permitType || !form.stepSubmissions?.length) {
      return []
    }

    const groups: IReportSubmissionGroup[] = []

    for (const step of permitType.workflowSteps) {
      if (!step?.id || !step.requireAllSubmissions) continue

      const stepSubmissions = form.getLatestRoundSubmissions(step.id)
      if (stepSubmissions.length === 0) continue

      const enrichedSubmissions = stepSubmissions.map(submission =>
        this.enrichSubmission(submission, step),
      )

      groups.push({
        stepId: step.id,
        stepName: this.getStepName(step),
        requireAllSubmissions: true,
        submissions: enrichedSubmissions,
      })
    }

    return groups
  }

  private enrichSubmission(
    submission: IStepSubmission,
    step: IWorkflowStep,
  ): IEnrichedSubmission {
    const user = this.projectMembersStore.getById(submission.userId)
    const userName = user?.fullName || Localization.translator.unknownUser
    const userProject = user
      ? this.userProjectsStore?.getByUser(user)
      : undefined
    const userCompany = userProject?.companyId
      ? this.companiesStore?.getCompanyNameById(userProject.companyId) || ''
      : ''

    const submittedAtFormatted =
      this.projectDateStore.getMonthDayYearAndTimeToDisplay(
        new Date(submission.submittedAt),
      )

    return {
      stepId: submission.stepId,
      stepName: this.getStepName(step),
      userId: submission.userId,
      userName,
      userCompany,
      submittedAtFormatted,
      fields: submission.fields || [],
    }
  }

  private getStepName(step: IWorkflowStep): string {
    return getWorkflowStepDisplayName(step.type)
  }

  private getSitemapImagesForReport(
    selectedLocations: ISiteLocation[],
  ): string[] {
    const selectedSitemapItems = this.sitemapItemsStore.list.filter(item =>
      selectedLocations?.some(({ id }) => id === item.assignedId),
    )

    return this.formsSitemaps
      .filter(s => {
        if (this.isProjectOverviewSitemap(s.id)) {
          return true
        }

        const assignedAttributesIds: string[] = this.getAssignedAttributesIds(
          s.id,
        )
        return selectedSitemapItems.some(item =>
          assignedAttributesIds.includes(item.assignedId),
        )
      })
      .map(s => s.filledImage)
  }

  private getAssignedAttributesIds(sitemapId: string): string[] {
    const attributes = this.attributesBySitemaps[sitemapId]
    if (attributes) {
      return attributes
    }

    const { firstBuilding } = this.locationAttributesStore.buildingsStore
    return firstBuilding ? [firstBuilding.id] : []
  }

  private get formsSitemaps(): Sitemap[] {
    return this.eventsStore.appState.workflowsMapIdsList
      .map(map => this.sitemapsStore.byId.get(map.sitemapId))
      .filter(Boolean)
  }

  private isProjectOverviewSitemap(sitemapId: string): boolean {
    const projectLocation = this.locationAttributesStore.projectLocation
    return projectLocation?.isSitemapAssigned(sitemapId)
  }

  private get attributesBySitemaps(): { [sitemapId: string]: string[] } {
    return this.locationAttributesStore.allAttributes.reduce(
      (map, attribute) => {
        if (attribute) {
          this.formsSitemaps.forEach(sitemap => {
            if (attribute.isSitemapAssigned(sitemap.id)) {
              if (map[sitemap.id]) {
                map[sitemap.id].push(attribute.id)
              } else {
                map[sitemap.id] = [attribute.id]
              }
            }
          })
        }
        return map
      },
      {},
    )
  }
}
